import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

const React = window.EcommerceCore.vendors.React;
const { useState, useEffect, useMemo } = React;

const Icon = window.EcommerceCore.common.components.Icon;
const LightboxModule = window.EcommerceCore.vendors.Lightbox;
const LightboxComponent = LightboxModule?.default || LightboxModule;
const ZoomPlugin = LightboxModule?.Zoom;
const ThumbnailsPlugin = LightboxModule?.Thumbnails;

interface MtcProductGalleryProps {
  product: any;
  selectedVariation: any;
  catalogUrl?: string;
}

export const MtcProductGallery: React.FC<MtcProductGalleryProps> = ({
  product,
  selectedVariation,
  catalogUrl,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [zoomStyle, setZoomStyle] = useState<React.CSSProperties>({
    transform: "scale(1)",
    transformOrigin: "center center",
  });
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Aktif dil tespiti ve metinler
  const currentLang =
    (window as any).commonVariables?.language?.current || "tr";

  const labels = useMemo(() => {
    if (currentLang === "en") {
      return {
        downloadCatalog: "Download Catalog",
        downloading: "Downloading...",
        rateLimitError:
          "Too many download requests. Please wait a minute and try again.",
        failedError: "Failed to download catalog.",
      };
    }
    return {
      downloadCatalog: "Katalog İndir",
      downloading: "İndiriliyor...",
      rateLimitError:
        "Çok fazla indirme talebinde bulundunuz. Lütfen 1 dakika sonra tekrar deneyin.",
      failedError: "Katalog indirilemedi.",
    };
  }, [currentLang]);

  // Katalog bağlantısını DOM veya Store API'den çözümle
  const effectiveCatalogUrl = useMemo(() => {
    if (catalogUrl) return catalogUrl;
    return (
      product?.productAdditionalInfo?.catalogUrl ||
      product?.extensions?.product_additional_info?.catalog_url ||
      ""
    );
  }, [catalogUrl, product]);

  // Görselleri topla ve tekilleştir
  const images = useMemo(() => {
    const list: any[] = [];
    const seenKeys = new Set<string | number>();

    const addImg = (img: any) => {
      if (!img) return;
      const key = img.id || img.src;
      if (key && !seenKeys.has(key)) {
        seenKeys.add(key);
        list.push(img);
      }
    };

    if (selectedVariation?.mainImage) addImg(selectedVariation.mainImage);
    if (selectedVariation?.galleryImages) {
      selectedVariation.galleryImages.forEach(addImg);
    }
    if (product?.mainImage) addImg(product.mainImage);
    if (product?.galleryImages) {
      product.galleryImages.forEach(addImg);
    }

    return list;
  }, [product, selectedVariation]);

  // Varyasyon seçimi değiştiğinde görsel indexini sıfırla
  useEffect(() => {
    setActiveIndex(0);
  }, [selectedVariation]);

  const safeActiveIndex = activeIndex >= images.length ? 0 : activeIndex;
  const currentImage = images[safeActiveIndex];

  // Mouse hareketine göre büyüteç efekti
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({
      transformOrigin: `${x}% ${y}%`,
      transform: "scale(2.2)",
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setZoomStyle({
      transform: "scale(1)",
      transformOrigin: "center center",
    });
  };

  // İndirme işlemi & Rate Limiting yönetimi
  const handleDownloadCatalog = async (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    if (isDownloading || !effectiveCatalogUrl) return;

    setIsDownloading(true);

    try {
      const response = await fetch(effectiveCatalogUrl);

      if (response.status === 429) {
        alert(labels.rateLimitError);
        setIsDownloading(false);
        return;
      }

      if (!response.ok) {
        alert(labels.failedError);
        setIsDownloading(false);
        return;
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      let filename = "katalog.pdf";
      const disposition = response.headers.get("content-disposition");
      if (disposition) {
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match && utf8Match[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          const standardMatch = disposition.match(/filename="?([^";]+)"?/i);
          if (standardMatch && standardMatch[1]) {
            filename = decodeURIComponent(standardMatch[1]);
          }
        }
      }

      const tempLink = document.createElement("a");
      tempLink.href = blobUrl;
      tempLink.download = filename;
      document.body.appendChild(tempLink);
      tempLink.click();
      tempLink.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.location.href = effectiveCatalogUrl;
    } finally {
      setIsDownloading(false);
    }
  };

  const slides = useMemo(() => {
    return images.map((img) => ({
      src: img.src,
      alt: img.alt || product?.name || "",
    }));
  }, [images, product]);

  const plugins = useMemo(() => {
    const list = [];
    if (ZoomPlugin) list.push(ZoomPlugin);
    if (ThumbnailsPlugin) list.push(ThumbnailsPlugin);
    return list;
  }, []);

  if (images.length === 0) {
    return (
      <div className="mtc-product-gallery">
        <div className="mtc-gallery-main mtc-gallery-main--placeholder">
          <span>Görsel Yok</span>
        </div>

        {effectiveCatalogUrl && (
          <div className="mtc-catalog-download-wrapper">
            <a
              href={effectiveCatalogUrl}
              onClick={handleDownloadCatalog}
              className={`mtc-catalog-download-btn ${isDownloading ? "is-disabled" : ""}`}
              title={labels.downloadCatalog}
              download
            >
              <span className="mtc-catalog-download-btn__icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </span>
              <span className="mtc-catalog-download-btn__text">
                {isDownloading ? labels.downloading : labels.downloadCatalog}
              </span>
              <span className="mtc-catalog-download-btn__badge">PDF</span>
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mtc-product-gallery">
      {/* Ana Görsel Sahnesi */}
      <div
        className="mtc-gallery-main"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setLightboxIndex(safeActiveIndex)}
      >
        <img
          src={currentImage?.src || currentImage?.thumbnail}
          alt={currentImage?.alt || product?.name || ""}
          className={`mtc-gallery-main__img ${isHovered ? "is-zoomed" : ""}`}
          style={isHovered ? zoomStyle : undefined}
        />

        {/* Büyütme (Lightbox) Butonu */}
        <button
          type="button"
          className="mtc-gallery-main__expand-btn"
          onClick={(e) => {
            e.stopPropagation();
            setLightboxIndex(safeActiveIndex);
          }}
          aria-label="Görseli Büyüt"
          title="Görseli Büyüt"
        >
          <Icon iconName="fullscreen-outlined" className="icon--stroke-thick" />
        </button>
      </div>

      {/* Küçük Resimler (Thumbnails) */}
      {images.length > 1 && (
        <div className="mtc-gallery-thumbnails">
          {images.map((img, idx) => {
            const isActive = idx === safeActiveIndex;
            return (
              <button
                key={img.id || idx}
                type="button"
                className={`mtc-gallery-thumb ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveIndex(idx)}
              >
                <img
                  src={img.thumbnail || img.src}
                  alt={img.alt || `${product?.name || ""} - ${idx + 1}`}
                />
              </button>
            );
          })}
        </div>
      )}

      {/* GALERİNİN HEMEN ALTINDA: KATALOG İNDİR BUTONU */}
      {effectiveCatalogUrl && (
        <div className="mtc-catalog-download-wrapper">
          <a
            href={effectiveCatalogUrl}
            onClick={handleDownloadCatalog}
            className={`mtc-catalog-download-btn ${isDownloading ? "is-disabled" : ""}`}
            title={labels.downloadCatalog}
            download
          >
            <span className="mtc-catalog-download-btn__icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </span>
            <span className="mtc-catalog-download-btn__text">
              {isDownloading ? labels.downloading : labels.downloadCatalog}
            </span>
            <span className="mtc-catalog-download-btn__badge">PDF</span>
          </a>
        </div>
      )}

      {/* Lightbox Modal */}
      {LightboxComponent && lightboxIndex !== null && (
        <LightboxComponent
          open={lightboxIndex !== null}
          close={() => setLightboxIndex(null)}
          index={lightboxIndex}
          slides={slides}
          plugins={plugins}
        />
      )}
    </div>
  );
};
